/* eslint-disable no-console */
import { Sitemap } from "@iventis/domain-model/model/sitemap";
import { EMPTY_GUID } from "@iventis/utilities";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSitemapApiContext } from "../context/sitemap-api-context";
import { useSitemapRoutingContext } from "../context/sitemap-routing-context";
import { createEmptySitemap, hasSitemapChanged, isSitemapValid } from "../sitemap-helpers";

const SITEMAP_KEY = "sitemap";

/** Handles sitemap data fetching and modification */
export function useSitemap() {
    const queryClient = useQueryClient();
    const { getSitemap, updateSitemap, createSitemap, deleteSitemapVersion: deleteSitemapVersionApiRequest } = useSitemapApiContext();
    const { getSelectedSitemapId, setSelectedSitemapId } = useSitemapRoutingContext();

    const sitemapId = getSelectedSitemapId();

    // A new sitemap will use an empty uuid as it's id
    const isNewSitemap = sitemapId === EMPTY_GUID;

    // If sitemap is newly created then set the state to an empty one
    const [sitemap, setSitemap] = useState<Sitemap | null>(
        isNewSitemap ? createEmptySitemap() : queryClient.getQueryData<Sitemap>([SITEMAP_KEY, sitemapId]) ?? null
    );

    const { isLoading, data: savedSitemap, refetch } = useQuery(
        [SITEMAP_KEY, sitemapId],
        async () => {
            const response = await getSitemap(sitemapId);
            setSitemap(response);
            return response;
        },
        {
            enabled: sitemapId != null && !isNewSitemap,
        }
    );

    const updateMutation = useMutation(updateSitemap, {
        onMutate: () => {
            queryClient.setQueryData([SITEMAP_KEY, sitemapId], sitemap);
        },
    });

    const createMutation = useMutation(createSitemap, {
        onSuccess: (sitemap) => {
            queryClient.setQueryData([SITEMAP_KEY, sitemap.id], sitemap);
            setSelectedSitemapId(sitemap.id);
        },
    });

    const canSitemapSave = hasSitemapChanged(savedSitemap, sitemap) && isSitemapValid(sitemap);

    const saveSitemap = () => {
        if (isNewSitemap) {
            createMutation.mutateAsync(sitemap);
        } else {
            updateMutation.mutateAsync(sitemap);
        }
    };

    const deleteSitemapVersion = async (sitemapVersionId: string) => {
        const updatedSitemapVersion: Sitemap = {
            ...sitemap,
            versions: sitemap.versions.filter((svl) => svl.id !== sitemapVersionId),
        };
        setSitemap(updatedSitemapVersion);
        try {
            await deleteSitemapVersionApiRequest(sitemapVersionId, sitemapId);
        } catch (err) {
            console.error(err);
        }
    };

    return {
        sitemap,
        setSitemap,
        isSitemapLoading: isLoading && !isNewSitemap,
        isSaving: updateMutation.isLoading || createMutation.isLoading,
        saveSitemap,
        canSitemapSave,
        deleteSitemapVersion,
        refetchSitemap: refetch,
    };
}
